import type { CourseDetailsResponse } from '@golf-app/shared';

export interface CatalogCourse {
  id: number | string;
  club_name?: string;
  course_name?: string;
  location?: { city?: string; state?: string };
  tees?: { male?: CatalogTee[]; female?: CatalogTee[] };
}
export interface CatalogTee {
  tee_name: string;
  slope_rating: number;
  course_rating: number;
  par_total: number;
  holes?: Array<{ par: number; handicap: number; hole_number?: number; yards?: number | null }>;
}
export interface CourseResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  source: 'local' | 'catalog';
  catalog?: CatalogCourse;
}
export interface CourseLookupState<Selected> {
  query: string;
  status: 'idle' | 'loading' | 'results' | 'empty' | 'error';
  results: CourseResult[];
  warning: string | null;
  selecting: boolean;
  selectionError: string | null;
  selected: Selected | null;
}
interface LookupApi {
  searchCourses(query: string, signal?: AbortSignal): Promise<Array<{ id: string; name: string; city: string | null; state: string | null }>>;
  searchCoursesAPI(query: string, signal?: AbortSignal): Promise<{ courses?: CatalogCourse[] }>;
  getCourse(id: string, signal?: AbortSignal): Promise<CourseDetailsResponse>;
}

const SEARCH_ERROR = 'Course search is unavailable. Check your connection and try again.';
const PARTIAL_WARNING = 'Some courses could not be checked. Try again if yours is missing.';

function displayName(course: CatalogCourse): string {
  const club = course.club_name ?? '';
  const layout = course.course_name ?? '';
  return layout && layout.toLocaleLowerCase() !== club.toLocaleLowerCase() ? `${club} - ${layout}` : club;
}
function key(course: CourseResult): string {
  const normalize = (value: string | null) => (value ?? '').trim().toLocaleLowerCase().replace(/[‘’']/g, '');
  return [course.name, course.city, course.state].map(normalize).join('|');
}
function validRows(rows: CourseResult[]): CourseResult[] {
  if (rows.some((row) => typeof row.id !== 'string' || !row.id.trim()
    || typeof row.name !== 'string' || !row.name.trim()
    || (row.city != null && typeof row.city !== 'string')
    || (row.state != null && typeof row.state !== 'string'))) throw new Error('Invalid course search response');
  return rows;
}

/** A provider that hangs must not leave course lookup loading forever. */
async function bounded<T>(request: (signal: AbortSignal) => Promise<T>, parent: AbortSignal, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  parent.addEventListener('abort', abort, { once: true });
  if (parent.aborted) abort();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve().then(() => { controller.signal.throwIfAborted(); return request(controller.signal); }),
      new Promise<never>((_, reject) => {
        controller.signal.addEventListener('abort', () => reject(new Error('Course request canceled')), { once: true });
        if (controller.signal.aborted) reject(new Error('Course request canceled'));
        timer = setTimeout(abort, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
    parent.removeEventListener('abort', abort);
  }
}

/** One state owner for both web pickers. Cancellation plus generations also
 * protects against providers which finish after abort, and A -> B -> A sessions. */
export function createCourseLookup<Selected>(options: {
  api: LookupApi;
  getSessionKey(): string;
  debounceMs?: number;
  timeoutMs?: number;
}) {
  let state: CourseLookupState<Selected> = {
    query: '', status: 'idle', results: [], warning: null,
    selecting: false, selectionError: null, selected: null,
  };
  let enabled = true;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: AbortController | undefined;
  const listeners = new Set<() => void>();
  const publish = (patch: Partial<CourseLookupState<Selected>>) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };
  function cancel() {
    generation += 1;
    clearTimeout(timer);
    pending?.abort();
    pending = undefined;
  }
  function ticket() {
    const version = generation;
    const session = options.getSessionKey();
    pending = new AbortController();
    return { signal: pending.signal, current: () => version === generation && session === options.getSessionKey() };
  }
  async function search(query: string, work: ReturnType<typeof ticket>) {
    if (!work.current()) return;
    const timeout = options.timeoutMs ?? 15_000;
    const [local, catalog] = await Promise.allSettled([
      bounded(async (signal) => {
        const rows = await options.api.searchCourses(query, signal);
        if (!Array.isArray(rows)) throw new Error('Invalid local course search response');
        return validRows(rows.map((row) => ({ ...row, source: 'local' as const })));
      }, work.signal, timeout),
      bounded(async (signal) => {
        const response = await options.api.searchCoursesAPI(query, signal);
        if (!response || !Array.isArray(response.courses)) throw new Error('Invalid catalog course search response');
        return validRows(response.courses.map((course) => ({
          id: course.id == null ? '' : String(course.id), name: displayName(course),
          city: course.location?.city ?? null, state: course.location?.state ?? null,
          source: 'catalog' as const, catalog: course,
        })));
      }, work.signal, timeout),
    ]);
    if (!work.current()) return;
    const catalogRows = catalog.status === 'fulfilled' ? catalog.value : [];
    const catalogKeys = new Set(catalogRows.map(key));
    const localRows = local.status === 'fulfilled' ? local.value.filter((row) => !catalogKeys.has(key(row))) : [];
    const results = [...catalogRows, ...localRows];
    const failed = local.status === 'rejected' || catalog.status === 'rejected';
    publish({
      results, status: results.length ? 'results' : failed ? 'error' : 'empty',
      warning: failed ? results.length ? PARTIAL_WARNING : SEARCH_ERROR : null,
    });
  }
  function setQuery(query: string) {
    cancel();
    const active = enabled && query.trim().length >= 2;
    publish({ query, selected: null, results: [], status: active ? 'loading' : 'idle', warning: null, selecting: false, selectionError: null });
    if (active) {
      const work = ticket();
      timer = setTimeout(() => void search(query.trim(), work), options.debounceMs ?? 300);
    }
  }
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    setQuery,
    retry() { setQuery(state.query); },
    setEnabled(value: boolean) {
      if (enabled === value) return;
      enabled = value;
      cancel();
      publish({ status: 'idle', warning: null, selecting: false, selectionError: null, results: [] });
      if (enabled && !state.selected) setQuery(state.query);
    },
    clear() { setQuery(''); },
    reset() { cancel(); publish({ query: '', selected: null, results: [], status: 'idle', warning: null, selecting: false, selectionError: null }); },
    suspend() { cancel(); },
    async select(row: CourseResult, hydrate: (row: CourseResult, full?: CourseDetailsResponse) => Selected) {
      if (!enabled || !state.results.includes(row)) return;
      cancel();
      const work = ticket();
      publish({ status: 'idle', warning: null, selecting: true, selectionError: null });
      try {
        const full = row.source === 'local'
          ? await bounded((signal) => options.api.getCourse(row.id, signal), work.signal, options.timeoutMs ?? 15_000)
              .catch(() => { throw new Error('Could not load this course. Check your connection and select it again.'); })
          : undefined;
        if (!work.current()) return;
        const selected = hydrate(row, full);
        publish({ selected, selecting: false, results: [] });
      } catch (error) {
        if (!work.current()) return;
        publish({ selecting: false, selectionError: error instanceof Error ? error.message : 'Could not load this course. Try again.' });
      }
    },
  };
}
